/**
 * How the tracking vocabulary is worded on screen, and what can be read off a
 * `TrackingResponse`.
 *
 * The rules themselves live in `@wend/shared` and are never re-implemented
 * here. In particular **`evaluateTrackingChange()` is imported, not
 * re-derived**: the API calls it to decide whether a write happens and this
 * screen calls it to decide whether a switch is even offered, and two
 * implementations of "may this tenant turn that on" drift — the way they drift
 * is that one of them stops refusing. Same argument as `sesReadiness()` in
 * `lib/integration-display.ts`, and the same conclusion. This file decides only
 * what each state is *called* and which semantic tone it carries.
 *
 * ## The two things this file will not do
 *
 * **It will not paraphrase the disclosure.** `TRACKING_DISCLOSURE` and
 * `VDM_COST_DISCLOSURE` arrive on the wire, on `trackingResponseSchema`, and
 * are rendered verbatim. That is the whole point of carrying a few hundred
 * bytes of prose on every read: the sentence in the API's audit entry and the
 * sentence on the screen are provably the same one, so "what were they told"
 * has an answer that does not depend on which frontend build was deployed. A
 * helper here that shortened it for layout would repeal that in one line, so
 * the shortening is not available — the components are laid out around the full
 * text instead.
 *
 * **It will not invent a state.** Every function below is a total mapping over
 * a union that already exists: the four redirect-domain states, the five domain
 * failures, the five refusals. There is no `default:` branch anywhere and no
 * fallback string, so adding a member to any of those enums in `@wend/shared`
 * breaks this file at compile time rather than rendering an empty span at a
 * tenant.
 *
 * ## Why the redirect domain's four states are worded so differently
 *
 * `pending` and `broken` carry the same fields and describe the same DNS
 * record, and they are worded as though they were unrelated, because they are.
 * `pending` is a setup step that has not finished — calm, expected, "this takes
 * a few minutes". `broken` is an **incident**: links in mail that has already
 * been delivered now point at a host that does not resolve, and every one of
 * those messages is out there getting worse. Showing "DNS changes take a few
 * minutes" to somebody in the second situation is the specific failure the
 * four-state union in `@wend/shared` was built to prevent, and this file is
 * where that prevention either happens or is quietly thrown away.
 */
import {
  SET_UP_DELIVERY_REPORTS_LABEL,
  TRACKING_CHANGE_REFUSAL_MESSAGES,
  TRACKING_DEFAULT_REDIRECT_HOST,
  TRACKING_DOMAIN_FAILURE_MESSAGES,
  evaluateTrackingChange,
  type DeliveryReportsSet,
  type TrackingChangeDecision,
  type TrackingDomainFailure,
  type TrackingRedirectDomainState,
  type TrackingResponse,
  type TrackingSettings,
  type TrackingSettingsUpdate,
} from '@wend/shared';
import type { BadgeTone, CalloutTone } from '@wend/ui';

/* -------------------------------------------------------------------------- */
/* The tracking switch, as a sentence                                          */
/* -------------------------------------------------------------------------- */

export interface TrackingBadge {
  readonly label: string;
  readonly tone: BadgeTone;
}

/**
 * The badge on the tracking card's header.
 *
 * **"Off" is not a warning and is not grey-as-in-broken.** A tenant whose
 * tracking is off is in the state this product ships in and the state it
 * defends; painting that amber would tell every one of them they had something
 * left to do. It is `neutral`, and the card's body states the promise that goes
 * with it rather than an invitation to fix it.
 *
 * The two half-on states get their own label rather than a shared "On", because
 * they do different things to a recipient's mail — one adds an image, the other
 * rewrites every link — and a person auditing this screen for a promise they
 * made needs to see which. Neither is green: a green tick beside "we add a
 * tracking pixel to your mail" would be the screen having an opinion about
 * whether that is good.
 */
export function trackingBadge(settings: TrackingSwitchState): TrackingBadge {
  if (settings.open && settings.click) return { label: 'Opens and clicks', tone: 'info' };
  if (settings.click) return { label: 'Clicks only', tone: 'info' };
  if (settings.open) return { label: 'Opens only', tone: 'info' };
  return { label: 'Off', tone: 'neutral' };
}

/**
 * The two switches, and nothing else about the settings.
 *
 * Narrower than `TrackingSettings` on purpose: what a recipient receives is
 * decided by these two booleans, and a function that took the whole object could
 * quietly start answering differently because of the redirect domain or the
 * configuration set — neither of which changes what is inside the message.
 */
export type TrackingSwitchState = Pick<TrackingSettings, 'open' | 'click'>;

export interface TrackingStateLine {
  readonly badge: TrackingBadge;
  /** What this workspace's recipients actually receive, in one sentence. */
  readonly says: string;
}

/**
 * Whether this workspace is tracking, said in one sentence for a place that has
 * room for one.
 *
 * **Why this exists at all, given `TrackingCard` already says it.** Slice 16
 * moved the tracking controls behind the Email tab's collapsed **Advanced**
 * disclosure. That was the right call for the *controls* — none of them decides
 * whether mail can leave — but it took the card's state callout with them, and
 * the disclosure's closed summary is a constant string that reads identically
 * whether a workspace tracks every recipient or none of them. A tenant mailing
 * cooperative banks who has promised their members that nothing tracks them
 * opens this page to confirm that promise, and until this line existed the page
 * they opened looked the same either way. Whether a pixel is in every message is
 * a fact about what every recipient receives, not an expert setting, so it is
 * stated where somebody who does not open anything will read it.
 *
 * **It is a summary of `TrackingCard`'s callout, not a second verdict.** Both
 * are driven by the same two booleans and the badge is literally the same value;
 * where more can be said — that Amazon does the injection on its side, when the
 * setting last changed, and, when tracking is off, the wire's own
 * `TRACKING_DISCLOSURE.off` verbatim — the card says it and this does not. If
 * the two ever read as though they disagreed, the card is the one that is right:
 * it prints the sentence the API records in its audit entry, and this one is
 * written here.
 *
 * The off sentence is a statement rather than an invitation. A workspace with
 * tracking off is in the state this product ships in and defends, and phrasing
 * it as something left undone would tell every one of them they had a job to do.
 */
export function trackingStateLine(settings: TrackingSwitchState): TrackingStateLine {
  const badge = trackingBadge(settings);

  if (settings.open && settings.click) {
    return {
      badge,
      says:
        'Every message this workspace sends carries an invisible 1×1 image, and every link in it is rewritten to pass through a redirect.',
    };
  }
  if (settings.open) {
    return {
      badge,
      says:
        'Every message this workspace sends carries an invisible 1×1 image. Links are not rewritten.',
    };
  }
  if (settings.click) {
    return {
      badge,
      says:
        'Every link in this workspace’s messages is rewritten to pass through a redirect. No tracking image is added.',
    };
  }
  return { badge, says: 'Nothing is added to the messages this workspace sends.' };
}

/* -------------------------------------------------------------------------- */
/* The redirect domain, in its four states                                     */
/* -------------------------------------------------------------------------- */

export interface RedirectDomainDisplay {
  readonly badge: TrackingBadge;
  /** The heading over the panel's explanation. Always a full sentence. */
  readonly title: string;
  /** The tone of the block that explanation sits in, or null for plain prose. */
  readonly tone: CalloutTone | null;
  /** True when the CNAME record should be on screen to be copied. */
  readonly showsRecord: boolean;
  /** True when "Check now" is worth offering — i.e. DNS is what we are waiting on. */
  readonly offersCheck: boolean;
}

/**
 * How each of the four states is announced.
 *
 * `not_configured` has **no tone at all**, deliberately. It is the state every
 * tenant starts in and the state a tenant who never wants click tracking stays
 * in for ever; a warning triangle there would manufacture an outstanding task
 * out of a settled decision. The other three all have something at stake and
 * all get a tone.
 *
 * `broken` is the only `bad` on this screen, and it is the only one that has
 * earned it — see the file header.
 */
export function readRedirectDomain(state: TrackingRedirectDomainState): RedirectDomainDisplay {
  switch (state.state) {
    case 'not_configured':
      return {
        badge: { label: 'Not set up', tone: 'neutral' },
        title: 'No redirect subdomain yet',
        tone: null,
        showsRecord: false,
        offersCheck: false,
      };
    case 'pending':
      return {
        badge: { label: 'Waiting for DNS', tone: 'warn' },
        title: 'Create this record with your DNS provider',
        tone: 'info',
        showsRecord: true,
        offersCheck: true,
      };
    case 'verified':
      return {
        badge: { label: 'Verified', tone: 'good' },
        title: 'This subdomain is verified',
        tone: 'good',
        showsRecord: true,
        offersCheck: false,
      };
    case 'broken':
      return {
        badge: { label: 'Stopped working', tone: 'bad' },
        // Not a question and not softened. Somebody has to act on this today.
        title: 'This subdomain has stopped resolving, and links in mail already sent are broken',
        tone: 'bad',
        showsRecord: true,
        offersCheck: true,
      };
  }
}

/**
 * The heading over a domain failure, paired with the message from
 * `@wend/shared` that says what to do about it.
 *
 * The messages are not repeated here — `TRACKING_DOMAIN_FAILURE_MESSAGES` is
 * the one copy of them and it is what the API would quote in a support
 * conversation. What is here is the six-word version that goes above them,
 * because a Callout with a paragraph and no heading is a paragraph people skim.
 *
 * `unavailable` is worded as "we do not know", not as a failure of the record,
 * and that distinction is the reason it is a separate member: a check that
 * could not run says nothing whatever about whether the CNAME is correct, and a
 * screen that turned it into a red cross would send somebody to edit DNS that
 * was already right.
 */
export const TRACKING_DOMAIN_FAILURE_HEADING: Readonly<Record<TrackingDomainFailure, string>> = {
  awaiting_dns: 'Waiting for the record to propagate',
  record_not_found: 'No record found at that name',
  record_mismatch: 'A record is there, pointing somewhere else',
  certificate_pending: 'Amazon is issuing the certificate',
  unavailable: 'The check could not run',
};

export function trackingDomainFailureMessage(failure: TrackingDomainFailure): string {
  return TRACKING_DOMAIN_FAILURE_MESSAGES[failure];
}

/**
 * Whether a failure is somebody's *fault* or merely somebody's *wait*.
 *
 * Drives the tone of the block, and only that. `awaiting_dns` and
 * `certificate_pending` both mean "correct, not finished" and must not be
 * painted the same as `record_not_found`, which means "go and edit your DNS".
 * `unavailable` is in the calm group because it is a statement about Wend, not
 * about the tenant's record.
 */
export function domainFailureNeedsAction(failure: TrackingDomainFailure): boolean {
  return failure === 'record_not_found' || failure === 'record_mismatch';
}

/* -------------------------------------------------------------------------- */
/* What the switches may currently do                                          */
/* -------------------------------------------------------------------------- */

/**
 * The decision for one candidate change, from the shared evaluator.
 *
 * This exists so a component can ask "would this be refused, and what would it
 * be told" *before* rendering a switch, rather than offering a control whose
 * only outcome is an error. The evaluator is pure and cheap, so asking it once
 * per switch per render is free.
 *
 * `now` is pinned to a fixed instant rather than left to default. The evaluator
 * stamps `updatedAt` and, for a first VDM acknowledgement, `costAcknowledgedAt`
 * — timestamps this screen never renders and never sends, because the value
 * that gets stored is the one the *API* computes when it runs the evaluator on
 * its own side. Feeding a live `new Date()` here would make an otherwise pure
 * render produce a different object every frame for no reason at all.
 */
const EVALUATION_INSTANT = new Date(0);

export function wouldAllow(
  settings: TrackingSettings,
  sesConnected: boolean,
  requested: TrackingSettingsUpdate,
): TrackingChangeDecision {
  return evaluateTrackingChange({
    current: settings,
    requested,
    sesConnected,
    now: EVALUATION_INSTANT,
  });
}

/**
 * The update body that leaves the current settings untouched.
 *
 * Every control on the screen starts from this and changes exactly one field,
 * which is what makes `PUT /tracking`'s whole-body shape workable: the caller
 * never has to remember which of the other two switches were on.
 *
 * VDM's already-acknowledged cost is carried through as `costAcknowledged:
 * true`, and that is not a loophole. `evaluateTrackingChange` reads the *stored*
 * `costAcknowledgedAt` when VDM is already on and keeps it — re-acknowledging
 * does not move the record of when somebody was actually told. Turning VDM on
 * from off still requires the acknowledgement to be produced by the screen that
 * showed the price, because `current.vdm.enabled` is false there and this
 * function returns `enabled: false`.
 */
export function unchangedUpdate(settings: TrackingSettings): TrackingSettingsUpdate {
  return {
    open: settings.open,
    click: settings.click,
    vdm: settings.vdm.enabled ? { enabled: true, costAcknowledged: true } : { enabled: false },
  };
}

/**
 * The message a refused change shows.
 *
 * Imported wholesale from `@wend/shared` rather than re-worded, for the reason
 * the disclosure is: the sentence the API logs when it refuses and the sentence
 * the person reads are the same sentence. `TRACKING_CHANGE_REFUSAL_MESSAGES` is
 * already keyed by the refusal, so this is a one-line re-export whose value is
 * that it is the *only* line in this app allowed to produce refusal copy.
 */
export function trackingRefusalMessage(decision: TrackingChangeDecision): string | null {
  return decision.allowed ? null : TRACKING_CHANGE_REFUSAL_MESSAGES[decision.reason];
}

/* -------------------------------------------------------------------------- */
/* Money                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * A per-thousand price, as a string with the currency on it.
 *
 * Two decimal places and a leading `about`, supplied by the caller's sentence
 * rather than baked in here — these are approximations of AWS's list price,
 * they are region-dependent, and AWS changes them. A product that prints a
 * stale figure as though it were a quote has told a customer something about
 * their money that is not true, so every place these appear says "about" and
 * links to AWS's own pricing page.
 *
 * `Intl.NumberFormat` with an explicit `en-US`/`USD` pair rather than the
 * browser's locale: the number is dollars on an AWS invoice regardless of who
 * is reading it, and rendering it as `₹0.07` to a reader in Mumbai — which is
 * what the default locale would do to the *symbol* — would be a lie about a
 * currency rather than a formatting preference.
 */
const USD_PER_THOUSAND = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatPerThousandUsd(value: number): string {
  return USD_PER_THOUSAND.format(value);
}

/**
 * The VDM surcharge as a share of what sending already costs.
 *
 * On the screen because "$0.07" and "$0.10" side by side are two small numbers
 * a person reads as small, and "a 70% increase on what you already pay Amazon
 * to send" is the same fact in the form somebody can actually decide against.
 * Rounded to whole percent, because the inputs are approximations and a decimal
 * place would imply a precision they do not have.
 *
 * Returns null when the send cost is zero, rather than dividing by it. That
 * cannot happen with the current constants and is not defended against here out
 * of caution — it is defended against because the two figures arrive on the
 * wire from `trackingResponseSchema`, whose only constraint is `.min(0)`.
 */
export function vdmSurchargeShare(perThousand: number, sendPerThousand: number): string | null {
  if (sendPerThousand <= 0) return null;
  return `${Math.round((perThousand / sendPerThousand) * 100)}%`;
}

/* -------------------------------------------------------------------------- */
/* Reading the whole response                                                  */
/* -------------------------------------------------------------------------- */

export interface TrackingReading {
  readonly settings: TrackingSettings;
  readonly badge: TrackingBadge;
  /** True when this tenant's mail carries a pixel, a rewritten link, or both. */
  readonly tracks: boolean;
  /**
   * The refusal that stands between this tenant and turning tracking on at all,
   * or null when nothing does.
   *
   * Deliberately narrow. Whether *click* may be switched on is a different
   * question with a different answer and its own panel, and is asked separately
   * by {@link wouldAllow} at the point the click switch is rendered. Folding
   * both into one field is how a card ends up telling somebody that open
   * tracking is unavailable because they have not verified a subdomain.
   */
  readonly blockedBy: string | null;
}

/**
 * Everything the card needs to render, derived once.
 *
 * `blockedBy` is computed by asking the shared evaluator what would happen if
 * open tracking were switched on, which is the cheapest question that surfaces
 * every *precondition* refusal — no SES connection, nothing provisioned —
 * without surfacing the click-specific ones, which have their own panel and
 * their own copy. A card that showed "click tracking needs a verified
 * subdomain" as the reason the *open* switch was disabled would be a card
 * sending somebody to fix the wrong thing.
 *
 * `tracks` is `settings.open || settings.click` and is deliberately taken from
 * the shared `tracksEngagement` predicate's own definition rather than
 * re-derived with a different one — it is the condition slice 4's sentence is
 * printed under, and there must be exactly one of it.
 */
export function readTracking(response: TrackingResponse): TrackingReading {
  const { settings, sesConnected } = response;

  const openOnly = wouldAllow(settings, sesConnected, {
    ...unchangedUpdate(settings),
    open: true,
    click: false,
  });

  return {
    settings,
    badge: trackingBadge(settings),
    tracks: settings.open || settings.click,
    blockedBy: trackingRefusalMessage(openOnly),
  };
}

/**
 * Re-exported so a component can name the host in its own prose without
 * importing `@wend/shared` for one string, and — more to the point — so there
 * is no temptation to type `awstrack.me` into a JSX literal where a future
 * change to AWS's host would leave it behind as a confident-sounding lie.
 */
export { TRACKING_DEFAULT_REDIRECT_HOST };

/* -------------------------------------------------------------------------- */
/* Delivery reports — slice 19                                                 */
/* -------------------------------------------------------------------------- */

/** What the delivery reports status says. Data, so it is asserted without a DOM. */
export interface DeliveryReportsDisplay {
  readonly tone: CalloutTone;
  readonly title: string;
  readonly body: string;
  /** When the set was first created, for the caption; `null` when there is none. */
  readonly since: string | null;
  readonly actionLabel: string;
}

/**
 * Whether this workspace's email comes back with delivery reports, in words.
 *
 * Total over `DeliveryReportsSet`'s two arms, like everything above. The set-up
 * arm names the configuration set, because it is a real object in the tenant's
 * AWS account that somebody may one day find in the console and wonder about;
 * and it says reports arrive only once the topic's subscription is confirmed,
 * because that is a fact this screen cannot see and "set up" must not be read as
 * "working". The not-set-up arm says what is missing in the tenant's terms —
 * delivered, bounced, marked as spam — rather than in AWS's.
 *
 * The action label is the button's, and the API's refusal for a set somebody
 * else's destination has made unsafe tells the tenant to press "Set up delivery
 * reports"; the two are kept in step by the integrations suite.
 */
export function readDeliveryReports(deliveryReports: DeliveryReportsSet): DeliveryReportsDisplay {
  if (deliveryReports.provisioned) {
    return {
      tone: 'good',
      title: 'Delivery reports are set up',
      body: `Email this workspace sends with tracking off goes through the ${deliveryReports.name} configuration set in your AWS account, which reports deliveries, bounces and complaints to your topic and adds nothing to the message. With tracking on, the tracking configuration set reports them instead. Either way they reach Wend only once your topic’s subscription to the address below is confirmed.`,
      since: deliveryReports.provisionedAt,
      actionLabel: 'Check it again',
    };
  }
  return {
    tone: 'warn',
    title: 'Delivery reports are not set up',
    body: 'Until they are, Wend cannot tell whether the emails you send with tracking off were delivered, bounced or marked as spam. Save your SNS topic below and Wend sets them up straight away: a configuration set in your AWS account that reports those events and adds nothing to your messages. If you have already saved a topic, press the button to try again.',
    since: null,
    actionLabel: SET_UP_DELIVERY_REPORTS_LABEL,
  };
}
